import React, { useState, useEffect } from 'react';
import { collegeService, countryService, stateService, districtService, universityService } from '../../services/api';
import './AdminPage.css';

function AdminColleges() {
  const [colleges, setColleges] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [formData, setFormData] = useState({
    Name: '',
    Code: '',
    MobileNo: '',
    Email: '',
    Address: '',
    Location: '',
    Description: '',
    CountryId: '',
    StateId: '',
    DistrictId: '',
    UniversityId: '',
    Logo: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formData.CountryId) {
      loadStates();
    }
  }, [formData.CountryId]);

  useEffect(() => {
    if (formData.StateId) {
      loadDistricts();
    }
  }, [formData.StateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await collegeService.getColleges();
      setColleges(data);
    } catch (error) {
      alert('Error loading colleges: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [countriesData, universitiesData] = await Promise.all([
        countryService.getCountries(),
        universityService.getUniversities()
      ]);
      setCountries(countriesData);
      setUniversities(universitiesData);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const loadStates = async () => {
    try {
      const data = await stateService.getStates();
      setStates(data.filter(s => s.CountryId === parseInt(formData.CountryId)));
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      const data = await districtService.getDistricts();
      setDistricts(data.filter(d => d.StateId === parseInt(formData.StateId)));
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const handleAdd = () => {
    setEditingCollege(null);
    setFormData({
      Name: '',
      Code: '',
      MobileNo: '',
      Email: '',
      Address: '',
      Location: '',
      Description: '',
      CountryId: '',
      StateId: '',
      DistrictId: '',
      UniversityId: '',
      Logo: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (college) => {
    setEditingCollege(college);
    setFormData({
      Id: college.Id,
      Name: college.Name || '',
      Code: college.Code || '',
      MobileNo: college.MobileNo || '',
      Email: college.Email || '',
      Address: college.Address || '',
      Location: college.Location || '',
      Description: college.Description || '',
      CountryId: college.CountryId || '',
      StateId: college.StateId || '',
      DistrictId: college.DistrictId || '',
      UniversityId: college.UniversityId || '',
      Logo: college.Logo || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this college?')) {
      try {
        await collegeService.deleteCollege(id);
        loadData();
      } catch (error) {
        alert('Error deleting college: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await collegeService.saveCollege(formData);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving college: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, Logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header page-headeradmin">
        <h1>Colleges Management</h1>
        <button className="btn-primary" onClick={handleAdd}>Add College</button>
      </div>

      {loading && !colleges.length ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {colleges.map((college) => (
                <tr key={college.Id}>
                  <td>{college.Name}</td>
                  <td>{college.Code}</td>
                  <td>{college.Email}</td>
                  <td>{college.MobileNo}</td>
                  <td>{college.Location}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(college)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(college.Id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCollege ? 'Edit College' : 'Add College'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.Name}
                    onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={formData.Code}
                    onChange={(e) => setFormData({ ...formData, Code: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.Email}
                    onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Mobile No</label>
                  <input
                    type="text"
                    value={formData.MobileNo}
                    onChange={(e) => setFormData({ ...formData, MobileNo: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.Address}
                    onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.Location}
                    onChange={(e) => setFormData({ ...formData, Location: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={formData.CountryId}
                    onChange={(e) => setFormData({ ...formData, CountryId: e.target.value, StateId: '', DistrictId: '' })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.Id} value={country.Id}>{country.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    value={formData.StateId}
                    onChange={(e) => setFormData({ ...formData, StateId: e.target.value, DistrictId: '' })}
                    disabled={!formData.CountryId}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.Id} value={state.Id}>{state.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select
                    value={formData.DistrictId}
                    onChange={(e) => setFormData({ ...formData, DistrictId: e.target.value })}
                    disabled={!formData.StateId}
                  >
                    <option value="">Select District</option>
                    {districts.map((district) => (
                      <option key={district.Id} value={district.Id}>{district.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>University</label>
                  <select
                    value={formData.UniversityId}
                    onChange={(e) => setFormData({ ...formData, UniversityId: e.target.value })}
                  >
                    <option value="">Select University</option>
                    {universities.map((university) => (
                      <option key={university.Id} value={university.Id}>{university.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.Description}
                    onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {formData.Logo && (
                    <img src={formData.Logo} alt="Logo preview" className="image-preview" />
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminColleges;

